import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryParams, StringParam } from "use-query-params";
import { useHistory } from "react-router";

import { Card, Loader, Message, Title } from "shared/components";
import { IAPIState, IJSONAPIErrorCodes, IStatus } from "shared/types";
import { BoldText, CenterContent, FlexContainer, Icon } from "shared/styles";
import { ContentWidth } from "shared/styles/constants";
import { copy } from "shared/assets";
import { Spacer } from "shared/layout";

import {
  getPeopleBlockStatus,
  useAPIError,
  useClearActionMetaOnUnMount,
} from "Bands/utils";
import { BAND_PATHS } from "Bands/router";
import {
  answerEventRSVP,
  dataMetaSelector,
  dispatchGetEventRSVP,
  eventRSVPSelectorCreator,
  eventRSVPSMetaSelector,
} from "Bands/state";
import { BAND_ACTIONS, IBand, PeopleBlockStatus } from "Bands/types";
import { getStatus } from "shared/styles/defaults";
import { API_ENDPOINTS } from "shared/constants";
import { EventHeader } from "Bands/components";

const answerRSVPCopy = copy.peopleBlock.answerRSVP;

export const AnswerRSVPPage: React.FC = () => {
  const [{ token, status }] = useQueryParams({
    token: StringParam,
    status: StringParam,
  });
  const dispatch = useDispatch();
  const history = useHistory();

  const peopleBlockStatus = useMemo(
    () => (status ? getPeopleBlockStatus(status as PeopleBlockStatus) : null),
    [status]
  );

  const rsvpSelector = useMemo(
    () => eventRSVPSelectorCreator(token || ""),
    [token]
  );
  const rsvp = useSelector(rsvpSelector);
  const { api: rsvpMeta } = useSelector(eventRSVPSMetaSelector(token || ""));
  useClearActionMetaOnUnMount(API_ENDPOINTS.EVENT_RSVP(token || ""));

  useEffect(() => {
    if (token) {
      dispatchGetEventRSVP(dispatch, token);
    }
  }, [token, dispatch]);

  const { api: answerMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.answerEventRSVP)
  );
  useClearActionMetaOnUnMount(BAND_ACTIONS.answerEventRSVP);

  const [rsvpAnswered, setRsvpAnswered] = useState(false);

  useEffect(() => {
    if (!token || !status || !peopleBlockStatus) {
      history.replace(BAND_PATHS.CALENDAR);
      return;
    }

    if (!rsvpAnswered && rsvp) {
      dispatch(
        answerEventRSVP({
          id: token,
          status: status as PeopleBlockStatus,
        })
      );
      setRsvpAnswered(true);
    }
  }, [rsvp, rsvpAnswered, token, status, dispatch, history, peopleBlockStatus]);

  const isSuccess = useMemo(
    () =>
      rsvpMeta?.status === IAPIState.succeeded &&
      answerMeta?.status === IAPIState.succeeded,
    [rsvpMeta?.status, answerMeta?.status]
  );
  const isPending = useMemo(
    () =>
      rsvpMeta?.status === IAPIState.pending ||
      answerMeta?.status === IAPIState.pending,
    [rsvpMeta?.status, answerMeta?.status]
  );
  const hasFailed = useMemo(
    () =>
      rsvpMeta?.status === IAPIState.failed ||
      answerMeta?.status === IAPIState.failed,
    [rsvpMeta?.status, answerMeta?.status]
  );

  const { error } = useAPIError({
    apiError: answerMeta?.error,
    message: answerRSVPCopy.messages.alreadyAnswered,
    errorCode: IJSONAPIErrorCodes.NotEqualError,
  });

  const renderSuccess = () => (
    <Spacer size="m">
      <Title>{answerRSVPCopy.title}</Title>
      <FlexContainer>
        {peopleBlockStatus?.icon && peopleBlockStatus?.iconStatus && (
          <Icon
            as={peopleBlockStatus.icon}
            color={getStatus(peopleBlockStatus.iconStatus).iconColor}
          />
        )}
        <span>
          {answerRSVPCopy.messages.success}{" "}
          <BoldText>{peopleBlockStatus?.label}</BoldText>
        </span>
      </FlexContainer>
    </Spacer>
  );

  const renderError = () => (
    <Spacer size="m">
      <Message
        status={IStatus.error}
        message={
          typeof error === "string" && rsvp
            ? error.replace("%invitedBy", rsvp?.invitedBy)
            : answerRSVPCopy.messages.failed
        }
      />
    </Spacer>
  );

  const bandTitle = useMemo(() => {
    return (rsvp?.event?.band as IBand)?.title || "";
  }, [rsvp?.event?.band]);

  return (
    <CenterContent width={ContentWidth.default}>
      <Spacer size="m">
        {rsvp?.event && (
          <EventHeader event={rsvp?.event} titleSuffix={bandTitle} />
        )}
        <Card>
          <Spacer size="s">
            {isPending && <Loader />}
            {isSuccess && renderSuccess()}
            {hasFailed && renderError()}
          </Spacer>
        </Card>
      </Spacer>
    </CenterContent>
  );
};
